import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PURGE } from 'redux-persist';
import {
  AiSystemsForUserDTO,
  CurrentUser,
  RegisteredUsersDTO,
  TeamRolesDTO,
  TeamInvitationsDTO,
  TeamUsersDTO
} from '../../types/Team.types';
import { logout } from './authSlice';

type UserManagementState = {
  teamInvitations: TeamInvitationsDTO | null;
  teamInvitationsLoading: boolean;
  teamInvitationsError: string | null;
  teamMembers: RegisteredUsersDTO | null;
  teamMembersLoading: boolean;
  teamMembersError: string | null;
  teamUsers: TeamUsersDTO[] | null;
  teamUsersLoading: boolean;
  teamUsersError: string | null;
  teamRoles: TeamRolesDTO | null;
  teamRolesLoading: boolean;
  teamRolesError: string | null;
  isUserInvited: boolean;
  invitingUser: boolean;
  onlyShowInvitedUsers?: boolean;
  currentUser: CurrentUser | null;
  aiSystemsForUser: AiSystemsForUserDTO | null;
  aiSystemsForUserLoading: boolean;
  aiSystemsForUserError: string | null;
  isRolePerAiSystemChangedSuccess: boolean;
  isRolePerAiSystemChangedStart: boolean;
  isRolePerAiSystemChangedError: string | null;
  removeUserFromSingleAiSystemSuccess: boolean;
  removeUserFromSingleAiSystemLoading: boolean;
  removeUserFromSingleAiSystemError: string | null;
  deleteUserFromAccountSuccess: boolean;
  deleteUserFromAccountLoading: boolean;
  deleteUserFromAccountError: string | null;
  resendInvitationSuccess: boolean;
  resendInvitationLoading: boolean;
  resendInvitationError: string | null;
  assignAiSystemWithoutInvitationSuccess: boolean;
  assignAiSystemWithoutInvitationLoading: boolean;
  assignAiSystemWithoutInvitationError: string | null;
  removedLastSystemPerUserSuccess: boolean;
  removedLastSystemPerUserLoading: boolean;
  removedLastSystemPerUserError: string | null;
  sorting: {
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
  };
};

const initialState: UserManagementState = {
  teamInvitations: null,
  teamInvitationsLoading: false,
  teamInvitationsError: null,
  teamMembers: null,
  teamMembersLoading: false,
  teamMembersError: null,
  teamUsers: null,
  teamUsersLoading: false,
  teamUsersError: null,
  teamRoles: null,
  teamRolesLoading: false,
  teamRolesError: null,
  isUserInvited: false,
  invitingUser: false,
  onlyShowInvitedUsers: false,
  currentUser: null,
  aiSystemsForUser: null,
  aiSystemsForUserLoading: false,
  aiSystemsForUserError: null,
  isRolePerAiSystemChangedSuccess: false,
  isRolePerAiSystemChangedStart: false,
  isRolePerAiSystemChangedError: null,
  removeUserFromSingleAiSystemSuccess: false,
  removeUserFromSingleAiSystemLoading: false,
  removeUserFromSingleAiSystemError: null,
  deleteUserFromAccountSuccess: false,
  deleteUserFromAccountLoading: false,
  deleteUserFromAccountError: null,
  resendInvitationSuccess: false,
  resendInvitationLoading: false,
  resendInvitationError: null,
  assignAiSystemWithoutInvitationSuccess: false,
  assignAiSystemWithoutInvitationLoading: false,
  assignAiSystemWithoutInvitationError: null,
  removedLastSystemPerUserSuccess: false,
  removedLastSystemPerUserLoading: false,
  removedLastSystemPerUserError: null,
  sorting: {
    sortBy: '',
    sortOrder: 'asc',
  },
};

export const userManagementSlice = createSlice({
  name: 'userManagement',
  initialState,
  reducers: {
    fetchTeamInvitationsStart: (state) => {
      state.teamInvitations = null;
      state.teamInvitationsLoading = true;
      state.teamInvitationsError = null;
    },
    fetchTeamInvitationsSuccess: (state, action) => {
      state.teamInvitations = action.payload;
      state.teamInvitationsLoading = false;
    },
    fetchTeamInvitationsFailure: (state, action) => {
      state.teamInvitationsLoading = false;
      state.teamInvitationsError = action.payload;
    },
    fetchTeamMembersStart: (state) => {
      state.teamMembers = null;
      state.teamMembersLoading = true;
      state.teamMembersError = null;
    },
    fetchTeamMembersSuccess: (state, action) => {
      state.teamMembers = action.payload;
      state.teamMembersLoading = false;
    },
    fetchTeamMembersFailure: (state, action) => {
      state.teamMembersLoading = false;
      state.teamMembersError = action.payload;
    },
    fetchTeamUsersStart: (state) => {
      state.teamUsers = null;
      state.teamUsersLoading = true;
      state.teamUsersError = null;
    },
    fetchTeamUsersSuccess: (state, action: PayloadAction<TeamUsersDTO[]>) => {
      state.teamUsers = action.payload;
      state.teamUsersLoading = false;
    },
    fetchTeamUsersFailure: (state, action: PayloadAction<string>) => {
      state.teamUsersLoading = false;
      state.teamUsersError = action.payload;
    },
    fetchTeamRolesStart: (state) => {
      state.teamRolesLoading = true;
      state.teamRolesError = null;
    },
    fetchTeamRolesSuccess: (state, action: PayloadAction<TeamRolesDTO>) => {
      state.teamRoles = action.payload;
      state.teamRolesLoading = false;
    },
    fetchTeamRolesFailure: (state, action: PayloadAction<string>) => {
      state.teamRolesLoading = false;
      state.teamRolesError = action.payload;
    },
    setIsUserInvited: (state, action) => {
      state.isUserInvited = action.payload;
    },
    inviteUserStart: (state) => {
      state.invitingUser = true;
    },
    inviteUserSuccess: (state) => {
      state.invitingUser = false;
      state.isUserInvited = true;
    },
    inviteUserFailure: (state) => {
      state.invitingUser = false;
    },
    setOnlyShowInvitedUsers: (state, action) => {
      state.onlyShowInvitedUsers = action.payload;
    },
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
    },
    fetchAiSystemsForUserStart: (state, action) => {
      state.aiSystemsForUserLoading = true;
      state.aiSystemsForUserError = null;
    },
    fetchAiSystemsForUserSuccess: (state, action) => {
      state.aiSystemsForUser = action.payload;
      state.aiSystemsForUserLoading = false;
    },
    fetchAiSystemsForUserError: (state, action) => {
      state.aiSystemsForUserLoading = false;
      state.aiSystemsForUserError = action.payload;
    },
    setIsRolePerAiSystemChangedSuccess: (state, action) => {
      state.isRolePerAiSystemChangedSuccess = action.payload;
      state.isRolePerAiSystemChangedStart = false;
    },
    setIsRolePerAiSystemChangedStart: (state, action) => {
      state.isRolePerAiSystemChangedStart = action.payload;
      state.isRolePerAiSystemChangedSuccess = false;
    },
    setIsRolePerAiSystemChangedError: (state, action) => {
      state.isRolePerAiSystemChangedError = action.payload;
      state.isRolePerAiSystemChangedSuccess = false;
    },
    setRemoveUserFromSingleAiSystemSuccess: (state, action) => {
      state.removeUserFromSingleAiSystemSuccess = action.payload;
      state.removeUserFromSingleAiSystemLoading = false;
    },
    setRemoveUserFromSingleAiSystemLoading: (state, action) => {
      state.removeUserFromSingleAiSystemLoading = action.payload;
      state.removeUserFromSingleAiSystemSuccess = false;
    },
    setRemoveUserFromSingleAiSystemError: (state, action) => {
      state.removeUserFromSingleAiSystemError = action.payload;
      state.removeUserFromSingleAiSystemLoading = false;
    },
    fetchDeleteUserFromAccountStart: (state) => {
      state.deleteUserFromAccountLoading = true;
      state.deleteUserFromAccountSuccess = false;
      state.deleteUserFromAccountError = null;
    },
    fetchDeleteUserFromAccountSuccess: (state, action) => {
      state.deleteUserFromAccountSuccess = action.payload;
      state.deleteUserFromAccountLoading = false;
    },
    fetchDeleteUserFromAccountFailure: (state, action) => {
      state.deleteUserFromAccountLoading = false;
      state.deleteUserFromAccountError = action.payload;
    },
    resendInvitationStart: (state) => {
      state.resendInvitationLoading = true;
      state.resendInvitationError = null;
    },
    resendInvitationSuccess: (state) => {
      state.resendInvitationSuccess = true;
      state.resendInvitationLoading = false;
    },
    resendInvitationFailure: (state, action) => {
      state.resendInvitationLoading = false;
      state.resendInvitationError = action.payload;
    },
    assignAiSystemWithoutInvitationStart: (state) => {
      state.assignAiSystemWithoutInvitationLoading = true;
      state.assignAiSystemWithoutInvitationSuccess = false;
      state.assignAiSystemWithoutInvitationError = null;
    },
    setAssignAiSystemWithoutInvitationSuccess: (state, action) => {
      state.assignAiSystemWithoutInvitationSuccess = action.payload;
      state.assignAiSystemWithoutInvitationLoading = false;
    },
    assignAiSystemWithoutInvitationFailure: (state, action) => {
      state.assignAiSystemWithoutInvitationLoading = false;
      state.assignAiSystemWithoutInvitationError = action.payload;
    },
    removedLastSystemPerUserStart: (state) => {
      state.removedLastSystemPerUserLoading = true;
      state.removedLastSystemPerUserSuccess = false;
      state.removedLastSystemPerUserError = null;
    },
    removedLastSystemPerUserSuccess: (state) => {
      state.removedLastSystemPerUserSuccess = true;
      state.removedLastSystemPerUserLoading = false;
    },
    removedLastSystemPerUserFailure: (state, action) => {
      state.removedLastSystemPerUserLoading = false;
      state.removedLastSystemPerUserError = action.payload;
    },
    setSorting: (
      state,
      action: PayloadAction<{ sortBy: string; sortOrder: 'asc' | 'desc' }>
    ) => {
      state.sorting.sortBy = action.payload.sortBy;
      state.sorting.sortOrder = action.payload.sortOrder;
      // state.pagination.page = 1;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(logout, () => initialState)
      .addCase(PURGE, () => initialState);
  }
});

export const {
  fetchTeamInvitationsStart,
  fetchTeamInvitationsSuccess,
  fetchTeamInvitationsFailure,
  fetchTeamMembersStart,
  fetchTeamMembersSuccess,
  fetchTeamMembersFailure,
  fetchTeamUsersStart,
  fetchTeamUsersSuccess,
  fetchTeamUsersFailure,
  fetchTeamRolesStart,
  fetchTeamRolesSuccess,
  fetchTeamRolesFailure,
  setIsUserInvited,
  inviteUserStart,
  inviteUserSuccess,
  inviteUserFailure,
  setOnlyShowInvitedUsers,
  setCurrentUser,
  fetchAiSystemsForUserStart,
  fetchAiSystemsForUserSuccess,
  fetchAiSystemsForUserError,
  setIsRolePerAiSystemChangedSuccess,
  setIsRolePerAiSystemChangedStart,
  setIsRolePerAiSystemChangedError,
  setRemoveUserFromSingleAiSystemSuccess,
  setRemoveUserFromSingleAiSystemLoading,
  setRemoveUserFromSingleAiSystemError,
  fetchDeleteUserFromAccountStart,
  fetchDeleteUserFromAccountSuccess,
  fetchDeleteUserFromAccountFailure,
  resendInvitationStart,
  resendInvitationSuccess,
  resendInvitationFailure,
  assignAiSystemWithoutInvitationStart,
  setAssignAiSystemWithoutInvitationSuccess,
  assignAiSystemWithoutInvitationFailure,
  removedLastSystemPerUserStart,
  removedLastSystemPerUserSuccess,
  removedLastSystemPerUserFailure,
  setSorting,
} = userManagementSlice.actions;

export default userManagementSlice.reducer;
